import { useId } from 'react';
import { cn } from '@/lib/utils';

// AUT-39 visual fidelity pass: the exact wordmark asset supplied for
// design-input/signal-v3-auth/login.jpg, converted verbatim to JSX. This is
// a NEW component, not an edit of SignalLogo.tsx -- decisions/
// auth-screens-share-one-layout.md § Consequences keeps /accept-invitation on
// the old chrome and the old logo on purpose, and that screen is the sole
// reason both icon files survive this spec. Do not edit SignalLogo.tsx to
// "converge" the two; the divergence is deliberate.
//
// The two trailing bars (the `opacity="0.8"` masked group and the standalone
// `opacity="0.6"` path) are white here, not the `#5f6ffe` blue those same two
// shapes render as in SignalLogo.tsx -- that colour difference is the whole
// point of this asset and is preserved as supplied, not "fixed" to match the
// old logo.
//
// Mask ids are global to the document. useId() scopes every mask id (and
// its url(#...) reference) to this component instance, so two mounts of
// SignalWordmark on the page at once -- or an old instance still painting
// out during a route transition while a new one mounts -- never collide.
//
// WEB-007: sizing is caller-controlled via `className` (viewBox alone fixes
// the aspect ratio) rather than fixed `width`/`height` attributes, so
// AuthLayout can box it to the prototype's measured `h-[2.7rem]`.
interface SignalWordmarkProps {
  className?: string;
}

const SignalWordmark = ({ className }: SignalWordmarkProps) => {
  const uid = useId();
  const maskGlyphs = `signal-wordmark-glyphs-${uid}`;
  const maskBar = `signal-wordmark-bar-${uid}`;

  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 128 51"
      fill="none"
      className={cn('h-[51px] w-auto', className)}
    >
      <mask
        id={maskGlyphs}
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="128"
        height="51"
      >
        <path d="M0 0H127.939V50.676H0V0Z" fill="black" />
      </mask>
      <g mask={`url(#${maskGlyphs})`}>
        <path
          d="M0 28.3172H7.15866C7.15866 30.1163 8.54667 30.992 10.129 30.992C11.5822 30.992 12.9702 30.2193 12.9702 28.843C12.9702 27.2629 11.0097 26.805 8.61189 26.2239C4.97139 25.319 0.32384 24.2035 0.32384 18.4689C0.32384 13.5087 3.96839 10.6675 9.90769 10.6675C15.9464 10.6675 19.4092 13.7669 19.4092 18.8921H12.4503C12.4503 17.299 11.2629 16.5519 9.82449 16.5519C8.58805 16.5519 7.40064 17.0872 7.40064 18.2629C7.40064 19.6936 9.29915 20.1668 11.6708 20.7632C15.3779 21.7194 20.2256 22.9581 20.2256 28.7099C20.2256 34.2111 15.9986 37.0667 10.1492 37.0667C3.98413 37.0667 0 33.8076 0 28.3172Z"
          fill="white"
        />
        <path d="M22.6287 17.019H29.3213V36.4499H22.6287V17.019Z" fill="white" />
        <path
          d="M52.7275 17.0195V34.2919C52.7275 40.3981 48.7299 44.1802 42.312 44.1802C36.7982 44.1802 32.9377 41.3997 32.0436 36.7954H38.5028C38.9661 38.0939 40.6244 38.7937 42.2504 38.7937C44.493 38.7937 46.2952 37.3688 46.2952 35.0579V34.4781C44.8348 35.4046 43.1005 35.9367 41.2249 35.9367C35.6544 35.9367 31.5146 31.792 31.5146 26.1703C31.5146 20.6299 35.6544 16.3979 41.2249 16.3979C43.5768 16.3979 45.7065 17.2417 47.3536 18.6495L48.2023 17.0191L52.7275 17.0195ZM42.3363 30.4261C44.7395 30.4261 46.4688 28.6454 46.4688 26.1703C46.4688 23.7158 44.7444 21.9352 42.3363 21.9352C39.9327 21.9352 38.188 23.7158 38.188 26.1703C38.188 28.6454 39.9115 30.4261 42.3358 30.4261H42.3363Z"
          fill="white"
        />
        <path
          d="M75.2629 24.6024V36.4505H68.5703V25.5272C68.5703 23.2652 67.7206 22.1435 66.0362 22.1435C63.9493 22.1435 62.7488 23.6012 62.7488 26.0741V36.4505H56.0557V17.0196H60.7509L61.6702 19.0234C63.1172 17.4109 65.2748 16.4448 67.7872 16.4448C72.2994 16.4448 75.2629 19.6517 75.2629 24.6024Z"
          fill="white"
        />
        <path
          d="M99.0471 16.9989V36.4298H93.8729L93.3004 35.0561C91.7086 36.3448 89.6931 37.0873 87.4366 37.0873C81.4955 37.0873 77.2478 32.7533 77.2478 26.7146C77.2478 20.6966 81.4955 16.4133 87.4366 16.4133C89.7251 16.4133 91.7697 17.178 93.3737 18.5016L94.0591 16.9989H99.0471ZM92.6815 26.7299C92.6815 24.208 90.8387 22.314 88.3321 22.314C85.8102 22.314 83.9819 24.208 83.9819 26.7299C83.9819 29.2675 85.8246 31.1615 88.3321 31.1615C90.8387 31.1615 92.6815 29.2675 92.6815 26.7299Z"
          fill="white"
        />
        <path d="M102.364 9.50562H109.058V36.3366H102.364V9.50562Z" fill="white" />
        <path
          d="M29.3215 10.2091C29.3215 12.0433 27.8346 13.5298 26.0008 13.5298C24.1666 13.5298 22.6797 12.0433 22.6797 10.2091C22.6797 8.37494 24.1666 6.88843 26.0008 6.88843C27.8346 6.88843 29.3215 8.37494 29.3215 10.2091Z"
          fill="white"
        />
        <g opacity="0.8">
          <mask
            id={maskBar}
            style={{ maskType: 'alpha' }}
            maskUnits="userSpaceOnUse"
            x="121"
            y="0"
            width="7"
            height="37"
          >
            <path d="M121.245 0H127.939V36.3368H121.245V0Z" fill="black" />
          </mask>
          <g mask={`url(#${maskBar})`}>
            <path opacity="0.2" d="M121.245 0H127.939V36.3366H121.245V0Z" fill="white" />
          </g>
        </g>
        <path
          d="M63.111 45.1041C63.111 47.0642 61.6816 48.5256 59.707 48.5256C58.9204 48.5256 58.2219 48.2588 57.679 47.801L57.4721 48.3421H56.1794V39.5327H57.93V42.217C58.4301 41.8765 59.0364 41.6831 59.707 41.6831C61.6816 41.6831 63.111 43.1422 63.111 45.1041ZM61.3271 45.1041C61.3271 44.0872 60.581 43.3293 59.5636 43.3293C58.5471 43.3293 57.7991 44.089 57.7991 45.1041C57.7991 46.1076 58.5471 46.869 59.5636 46.869C60.581 46.869 61.3271 46.1112 61.3271 45.1041Z"
          fill="white"
        />
        <path
          d="M70.1481 41.8662L67.3253 48.2562C66.549 50.0112 65.8662 50.676 64.4134 50.676H63.692V49.1022H64.2983C65.1421 49.1022 65.3607 48.8769 65.7591 47.8982L65.7834 47.846L63.0398 41.8662H64.9455L66.6731 45.8499L68.286 41.8662H70.1481Z"
          fill="white"
        />
        <path
          d="M76.8885 41.8306V43.4822H76.1288C75.1415 43.4822 74.752 43.9193 74.752 44.9777V48.342H73.0015V41.8661H74.1241L74.4345 42.6595C74.8964 42.0869 75.4815 41.8306 76.3132 41.8306H76.8885Z"
          fill="white"
        />
        <path
          d="M83.6922 45.5944H78.7906C78.9426 46.4782 79.5237 46.9648 80.3733 46.9648C80.9814 46.9648 81.4807 46.677 81.747 46.2137H83.5865C83.1161 47.6256 81.877 48.5247 80.3733 48.5247C78.4613 48.5247 77.0144 47.0444 77.0144 45.1037C77.0144 43.1534 78.4514 41.6831 80.3733 41.6831C82.3622 41.6831 83.7255 43.2155 83.7255 45.0807C83.7255 45.2521 83.7138 45.423 83.6922 45.5944ZM78.8374 44.3984H81.9745C81.7276 43.6068 81.1591 43.1773 80.3733 43.1773C79.5939 43.1773 79.0406 43.6324 78.8374 44.3984Z"
          fill="white"
        />
        <path
          d="M84.25 45.0843C84.25 43.1435 85.7311 41.6831 87.6989 41.6831C89.3752 41.6831 90.6769 42.7459 90.9922 44.3395H89.2533C88.9691 43.7026 88.3862 43.3293 87.689 43.3293C86.7391 43.3293 86.0253 44.0912 86.0253 45.0942C86.0253 46.0968 86.7508 46.8785 87.689 46.8785C88.3961 46.8785 88.9556 46.4849 89.2515 45.7927H91.0138C90.7083 47.4205 89.3833 48.5247 87.689 48.5247C85.7401 48.5247 84.25 47.0247 84.25 45.0843Z"
          fill="white"
        />
        <path
          d="M91.4492 45.1041C91.4492 43.1395 92.9443 41.6831 94.9498 41.6831C96.9464 41.6831 98.4505 43.1422 98.4505 45.1041C98.4505 47.0566 96.9464 48.5251 94.9498 48.5251C92.9443 48.5251 91.4492 47.0566 91.4492 45.1041ZM96.6761 45.1041C96.6761 44.0552 95.9596 43.3293 94.9498 43.3293C93.941 43.3293 93.2245 44.0552 93.2245 45.1041C93.2245 46.153 93.941 46.8789 94.9498 46.8789C95.9596 46.8789 96.6761 46.153 96.6761 45.1041Z"
          fill="white"
        />
        <path
          d="M109.058 44.2811V48.3421H107.306V44.5703C107.306 43.7054 106.968 43.2497 106.333 43.2497C105.552 43.2497 105.088 43.8255 105.088 44.8397V48.3421H103.381V44.5703C103.381 43.7054 103.052 43.2497 102.425 43.2497C101.633 43.2497 101.161 43.8255 101.161 44.8397V48.3421H99.4102V41.8662H100.633L100.962 42.6695C101.421 42.0893 102.13 41.7124 102.893 41.7124C103.716 41.7124 104.398 42.0983 104.773 42.7491C105.215 42.1204 105.953 41.7124 106.777 41.7124C108.117 41.7124 109.058 42.7635 109.058 44.2811Z"
          fill="white"
        />
        <path
          opacity="0.6"
          d="M111.74 4.67651H118.434V36.3368H111.74V4.67651Z"
          fill="white"
        />
      </g>
    </svg>
  );
};

export default SignalWordmark;
